/**
 * GlobalNavigation hook for the Invited App Shortcuts GRS recommendations query.
 *
 * Unlike the GSN surface (PUMA-2011), which reads the query with platform Relay
 * (`useLazyLoadQuery` on the ambient AGG environment inside `GlobalSideNavigationAGGRelayConnection`),
 * the legacy nav is NOT wrapped in that environment and already reads its own
 * `GlobalNavigationComponentQuery` through `@confluence/apollo-relay-interop`. So this hook uses the
 * same interop path:
 *
 *   1. `preloadGlobalNavigationComponent` `queryAgg`s this operation at route resolution (SSR),
 *      seeding the interop (Apollo AGG) cache.
 *   2. This hook reads the SAME operation + variables with
 *      `useQueryInterop({ interopApolloAggClient: true })`, so the SSR-preloaded record hydrates the
 *      client cache and is served without a second network request.
 *
 * The hook is deliberately parent-level (called from `GlobalNavigationComponent`) rather than a
 * self-contained renderer: the parent needs `recommendedApps` to decide BOTH whether to render the
 * decorated invited-app nudge AND whether to hand the Jira shortcut over from the main
 * `GlobalAppShortcuts` section (which strips it when the nudge renders it). Keeping the read here
 * means both decisions use the same value.
 *
 * Everything downstream of the read (GRS response translation and the resolver) reuses the same
 * helpers as the GSN renderer, so the two surfaces stay behaviourally identical. They differ only
 * in how the query is read.
 */
import { useMemo } from 'react';

import {
	useInvitedAppShortcuts,
	type UseInvitedAppShortcutsResult,
} from '@atlassian/cross-join-nudges/services/invited-app-shortcuts/invited-app-shortcuts';
import type { InvitedAppShortcutRecommendedApp } from '@atlassian/cross-join-nudges/types';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import { useRecommendedApps } from '../PlatformNavigation/AppShortcuts/hooks/useRecommendedApps.tsx';
import { InvitedAppShortcutsRecommendationsQueryNode } from '../PlatformNavigation/AppShortcuts/InvitedAppShortcutsRecommendations.tsx';
import { buildInvitedAppShortcutsRecommendationsVariables } from '../PlatformNavigation/AppShortcuts/invitedAppShortcutsRecommendationsVariables.ts';
import type {
	InvitedAppShortcutsRecommendationsQuery$data,
	InvitedAppShortcutsRecommendationsQuery$variables,
} from '../PlatformNavigation/AppShortcuts/__generated__/InvitedAppShortcutsRecommendationsQuery.graphql.ts';

const SOURCE_APP = 'confluence' as const;

export type UseLegacyInvitedAppShortcutsRecommendationsArgs = {
	cloudId: string;
	userId: string;
	/** When false the read is skipped. */
	enabled: boolean;
};

export type UseLegacyInvitedAppShortcutsRecommendationsResult = {
	recommendedApps: ReadonlyArray<InvitedAppShortcutRecommendedApp>;
	resolverResult: UseInvitedAppShortcutsResult;
};

export function useLegacyInvitedAppShortcutsRecommendations({
	cloudId,
	userId,
	enabled,
}: UseLegacyInvitedAppShortcutsRecommendationsArgs): UseLegacyInvitedAppShortcutsRecommendationsResult {
	// Built from the shared builder so the values stay byte-identical to the preloader's — a mismatch
	// would miss the SSR-seeded interop cache record and trigger a client refetch.
	const variables = useMemo(
		() => buildInvitedAppShortcutsRecommendationsVariables(cloudId),
		[cloudId],
	);

	// Read through interop against the Apollo AGG client — the same path the legacy nav uses for its
	// own GlobalNavigationComponentQuery. Skipped when not `enabled`. On error/loading `data` is
	// undefined, which maps to no recommended apps (fails open to the plain menu).
	const { data } = useQueryInterop<
		InvitedAppShortcutsRecommendationsQuery$data,
		InvitedAppShortcutsRecommendationsQuery$variables
	>(InvitedAppShortcutsRecommendationsQueryNode, {
		interopApolloAggClient: true,
		variables,
		skip: !enabled,
	});

	// Shared with the nav4 GSN renderer. `data` is `undefined` on error/loading, which the hook maps
	// to no recommended apps (fails open to the plain menu) — see `useRecommendedApps` for why this
	// keys off the parsed `product` rather than the raw `entityId`.
	const { recommendedApps, targetProducts } = useRecommendedApps(data);

	// Eligibility comes from `recommendedApps`. Inline input (matching the GSN renderer) so the
	// discriminated union narrows.
	const resolverResult = useInvitedAppShortcuts(
		enabled
			? {
					enabled: true,
					sourceProduct: SOURCE_APP,
					targetProducts,
					cloudId,
					userId,
					recommendedApps,
				}
			: { enabled: false },
	);

	return { recommendedApps, resolverResult };
}
